import {
    AccountType,
    DefaultCashBasisAccountCodes,
} from "@common/constants/domains/accounting/accounts";

export enum CashFlowSection {
    Operating = "Operating",
    Investing = "Investing",
    Financing = "Financing",
}

const ACCOUNT_TYPE_TO_SECTION_TYPED: Record<AccountType, CashFlowSection> = {
    [AccountType.cash]: CashFlowSection.Operating,
    [AccountType.accountsReceivable]: CashFlowSection.Operating,
    [AccountType.inventory]: CashFlowSection.Operating,
    [AccountType.otherCurrentAssets]: CashFlowSection.Operating,
    [AccountType.accountsPayable]: CashFlowSection.Operating,
    [AccountType.creditCards]: CashFlowSection.Operating,
    [AccountType.payrollLiabilities]: CashFlowSection.Operating,
    [AccountType.otherCurrentLiabilities]: CashFlowSection.Operating,
    [AccountType.income]: CashFlowSection.Operating,
    [AccountType.cogs]: CashFlowSection.Operating,
    [AccountType.operatingExpenses]: CashFlowSection.Operating,
    [AccountType.otherIncome]: CashFlowSection.Operating,
    [AccountType.otherExpenses]: CashFlowSection.Operating,

    [AccountType.investments]: CashFlowSection.Investing,
    [AccountType.fixedAssets]: CashFlowSection.Investing,
    [AccountType.intangibleAssets]: CashFlowSection.Investing,
    [AccountType.otherAssets]: CashFlowSection.Investing,

    [AccountType.shortTermLoans]: CashFlowSection.Financing,
    [AccountType.longTermLoans]: CashFlowSection.Financing,
    [AccountType.otherLiabilities]: CashFlowSection.Financing,
    [AccountType.equity]: CashFlowSection.Financing,
};

const ACCOUNT_TYPE_TO_SECTION: Record<string, CashFlowSection> = {
    ...ACCOUNT_TYPE_TO_SECTION_TYPED,
};

export function classifyAccountType(accountType: string): CashFlowSection {
    const section = ACCOUNT_TYPE_TO_SECTION[accountType];
    if (!section) {
        throw new Error(
            `Unsupported account type for cash flow classification: ${accountType}`,
        );
    }
    return section;
}

const DEBIT_NORMAL_ACCOUNT_TYPES = new Set<string>([
    AccountType.cash,
    AccountType.accountsReceivable,
    AccountType.inventory,
    AccountType.otherCurrentAssets,
    AccountType.investments,
    AccountType.fixedAssets,
    AccountType.intangibleAssets,
    AccountType.otherAssets,
    AccountType.cogs,
    AccountType.operatingExpenses,
    AccountType.otherExpenses,
]);

export function getCashFlowSignMultiplier(accountType: string): number {
    return DEBIT_NORMAL_ACCOUNT_TYPES.has(accountType) ? -1 : 1;
}

interface AccountForCashFlowClassification {
    kickOriginalCode: number | null;
}

const NON_CASH_CONTRA_ASSET_CODES = new Set<number>([
    DefaultCashBasisAccountCodes.accumulatedDepreciation,
    DefaultCashBasisAccountCodes.accumulatedAmortization,
    DefaultCashBasisAccountCodes.accumulatedDepletion,
]);

export function isNonCashContraAssetAccount(
    account: AccountForCashFlowClassification,
): boolean {
    if (account.kickOriginalCode === null) {
        return false;
    }
    return NON_CASH_CONTRA_ASSET_CODES.has(account.kickOriginalCode);
}
